<template>
  <div v-if="!!entity">
    <div class="row mb-3 mt-3">
      <div class="col-sm-4">
        <h1>{{ entity.deviceNumber }}</h1>
      </div>
      <div class="col-sm-2 d-none d-lg-block"><h3>Naprava</h3></div>
      <div
        class="col-sm-6 text-end"
        v-if="
          !isNew &&
          entity.customerID &&
          entity.facilityID &&
          entity.customerID !== $EMPTY_GUID &&
          entity.facilityID !== $EMPTY_GUID &&
          canChange
        "
      >
        <span
          v-show="
            ['ColdRoomSystem', 'ColdRoomStandalone', 'FreezerRoomSystem', 'FreezerRoomStandalone'].includes(
              entity.typeText,
            )
          "
        >
          <button
            class="btn btn-warning me-3"
            type="button"
            :disabled="
              entity.dateLastOpenDoor &&
              $dayjs &&
              $dayjs.utc().diff($dayjs.utc(entity.dateLastOpenDoor), 'hour', true) < 2
            "
            @click="openDoorAsync"
          >
            Odprta vrata
          </button>
        </span>
        <button class="btn btn-primary me-3" type="button" @click="createServiceOrderAsync">Nov SDN</button>
        <router-link :to="{ name: 'CustomerEdit', params: { customerId: entity.customerID } }" class="link-warning ms-3"
          >Stranka</router-link
        >
        <router-link :to="{ name: 'FacilityEdit', params: { facilityId: entity.facilityID } }" class="link-success ms-3"
          >Objekt</router-link
        >
        <router-link
          :to="{ name: 'DeviceBarcode', params: { deviceId: entity.id } }"
          v-show="entity.deviceNumber"
          target="_blank"
          class="link-dark ms-3"
          >Črtna koda</router-link
        >
      </div>
    </div>

    <fieldset :disabled="!canChange">
      <div class="row mb-3">
        <div class="col-md-9">
          <div class="row mb-3" v-show="entity.canEnableDefrosting">
            <label class="form-check-label col-sm-4" for="is-defrosting">V odtaljevanju</label>
            <div class="col-sm-8">
              <div class="form-check form-switch">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="is-defrosting"
                  v-model="entity.isDefrosting"
                  @change="!entity.isDefrosting && (entity.defrostingNote = '')"
                />
              </div>
            </div>
          </div>
          <div class="row mb-3" v-show="entity.isDefrosting">
            <label class="col-form-label col-sm-4">Opomba za odtaljevanje</label>
            <div class="col-sm-8">
              <input class="form-control" type="text" v-model="entity.defrostingNote" />
            </div>
          </div>
          <div class="row">
            <div class="col">
              <SelectCustomerEntity
                required
                :facility-invalid="v.invalid('facilityID')"
                :customerID="entity.customerID"
                :facilityID="entity.facilityID"
                :canChange="isNew"
                @change="
                  (e) => {
                    entity.customerID = e.customerID
                    entity.facilityID = e.facilityID
                  }
                "
              />
            </div>
          </div>
          <SelectEnum
            class="row mb-3"
            required
            :showAll="false"
            v-model="entity.typeText"
            :types="deviceTypes"
            :invalid="v.invalid('typeText')"
            label="Vrsta naprave"
            :disabled="!isNew"
          />
          <div class="row mb-3">
            <label class="form-check-label col-sm-4" for="is-active">Aktivna</label>
            <div class="col-sm-8">
              <div class="form-check form-switch">
                <input class="form-check-input" type="checkbox" id="is-active" v-model="entity.isActive" />
              </div>
            </div>
          </div>
          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Opomba</label>
            <div class="col-sm-8">
              <input class="form-control" type="text" v-model="entity.note" />
            </div>
          </div>

          <div class="row mb-3" v-if="entity.typeText !== 'System'">
            <label class="col-form-label col-sm-4">Oznaka - nalepka</label>
            <div class="col-sm-8" v-if="isNew">
              <div class="input-group">
                <span class="input-group-text">{{ entityLabelLeft }}</span>
                <input
                  class="form-control"
                  :class="{ 'is-invalid': v.invalid('label') }"
                  type="text"
                  v-model="entityLabelRight"
                  required
                />
              </div>
            </div>
            <div class="col-sm-8" v-else>
              <input
                class="form-control"
                :class="{ 'is-invalid': v.invalid('label') }"
                type="text"
                v-model="entity.label"
                required
              />
            </div>
          </div>

          <div class="row mb-3" v-show="entity.typeShort !== 'THM' && entity.typeText !== 'System'">
            <label class="col-form-label col-sm-4">Dobavitelj</label>
            <div class="col-sm-8">
              <input class="form-control" type="text" v-model="entity.supplier" />
            </div>
          </div>
          <div class="row mb-3" v-show="entity.typeShort !== 'THM' && entity.typeText !== 'System'">
            <label class="col-form-label col-sm-4">Leto izdelave</label>
            <div class="col-sm-4 col-md-2">
              <input
                class="form-control"
                type="number"
                v-model="entity.yearOfManufacture"
                pattern="[0-9]{4}"
                required
              />
            </div>
          </div>
          <div class="row mb-3" v-show="entity.typeShort !== 'THM' && entity.typeText !== 'System'">
            <label class="col-form-label col-sm-4">Serijska številka</label>
            <div class="col-sm-8">
              <input class="form-control" type="text" v-model="entity.serialNumber" />
            </div>
          </div>
          <div class="row mb-3" v-if="entity.isSlave">
            <label class="col-form-label col-sm-4">Vezano na agregat</label>
            <div class="col-sm-8">
              <SelectEntity
                required
                :apiEntity="'DropDown/Devices?facilityId=' + entity.facilityID + '&type=CoolingUnit|Chiller|HeatPump'"
                v-model="entity.sourceDeviceID"
                :invalid="v.invalid('sourceDeviceID')"
              />
            </div>
          </div>

          <DateOnly
            class="row mb-3"
            v-model="entity.warrantyValidUntil"
            label="Garancija do"
            v-show="entity.typeShort !== 'THM' && entity.typeText !== 'System'"
          />

          <div class="row mb-3" v-show="entity.hasLeakageTest">
            <label class="col-form-label col-sm-4">Preverjanja uhajanja</label>
            <div class="col-sm-3">
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="leakageTestIntervalMonths"
                  id="leakageTestIntervalMonths0"
                  value="0"
                  v-model="entity.leakageTestIntervalMonths"
                />
                <label class="form-check-label" for="leakageTestIntervalMonths0">0</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="leakageTestIntervalMonths"
                  id="leakageTestIntervalMonths1"
                  value="3"
                  v-model="entity.leakageTestIntervalMonths"
                />
                <label class="form-check-label" for="leakageTestIntervalMonths1">3</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="leakageTestIntervalMonths"
                  id="leakageTestIntervalMonths3"
                  value="12"
                  v-model="entity.leakageTestIntervalMonths"
                />
                <label class="form-check-label" for="leakageTestIntervalMonths3">12</label>
              </div>
            </div>
            <DateOnly class="col-sm-5" v-if="entity.hasLeakageTest" v-model="entity.lastLeakageTest" />
          </div>

          <div class="row mb-3" v-show="entity.hasService && !isNew">
            <label class="col-form-label col-sm-4">Servis</label>
            <div class="col-sm-3">
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="regularServiceIntervalMonths"
                  id="regularServiceIntervalMonths0"
                  value="0"
                  v-model="entity.regularServiceIntervalMonths"
                />
                <label class="form-check-label" for="regularServiceIntervalMonths0">0</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="regularServiceIntervalMonths"
                  id="regularServiceIntervalMonths1"
                  value="12"
                  v-model="entity.regularServiceIntervalMonths"
                />
                <label class="form-check-label" for="regularServiceIntervalMonths1">12</label>
              </div>
            </div>
            <DateOnly class="col-sm-5" v-model="entity.lastService" />
          </div>

          <div class="row mb-3" v-show="(entity.hasCondenserCleaningWet || entity.hasCondenserCleaningDry) && !isNew">
            <label class="col-form-label col-sm-4" v-show="entity.hasCondenserCleaningDry"
              >Interval čiščenja kondenzatorja suho v [mesec]</label
            >
            <label class="col-form-label col-sm-4" v-show="entity.hasCondenserCleaningWet"
              >Interval čiščenja kondenzatorja mokro v [mesec]</label
            >
            <div class="col-sm-3">
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="condenserCleaningIntervalMonths"
                  id="condenserCleaningIntervalMonths0"
                  value="0"
                  v-model="entity.condenserCleaningIntervalMonths"
                />
                <label class="form-check-label" for="condenserCleaningIntervalMonths0">0</label>
              </div>
              <div class="form-check form-check-inline" v-show="entity.hasCondenserCleaningDry">
                <input
                  class="form-check-input"
                  type="radio"
                  name="condenserCleaningIntervalMonths"
                  id="condenserCleaningIntervalMonths1"
                  value="3"
                  v-model="entity.condenserCleaningIntervalMonths"
                />
                <label class="form-check-label" for="condenserCleaningIntervalMonths1">3</label>
              </div>
              <div class="form-check form-check-inline" v-show="entity.hasCondenserCleaningWet">
                <input
                  class="form-check-input"
                  type="radio"
                  name="condenserCleaningIntervalMonths"
                  id="condenserCleaningIntervalMonths2"
                  value="6"
                  v-model="entity.condenserCleaningIntervalMonths"
                />
                <label class="form-check-label" for="condenserCleaningIntervalMonths2">6</label>
              </div>
              <div class="form-check form-check-inline" v-show="entity.hasCondenserCleaningWet">
                <input
                  class="form-check-input"
                  type="radio"
                  name="condenserCleaningIntervalMonths"
                  id="condenserCleaningIntervalMonths3"
                  value="12"
                  v-model="entity.condenserCleaningIntervalMonths"
                />
                <label class="form-check-label" for="condenserCleaningIntervalMonths3">12</label>
              </div>
            </div>
            <DateOnly class="col-sm-5" v-model="entity.lastCondenserCleaning" />
          </div>

          <div class="row mb-3" v-show="entity.hasCO2Sensor && !isNew">
            <label class="col-form-label col-sm-4">CO2 senzor</label>
            <div class="col-sm-3">
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="cO2SensorIntervalMonths"
                  id="cO2SensorIntervalMonths1"
                  value="0"
                  v-model="entity.cO2SensorIntervalMonths"
                />
                <label class="form-check-label" for="cO2SensorIntervalMonths1">0</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="cO2SensorIntervalMonths"
                  id="cO2SensorIntervalMonths2"
                  value="12"
                  v-model="entity.cO2SensorIntervalMonths"
                />
                <label class="form-check-label" for="cO2SensorIntervalMonths2">12</label>
              </div>
            </div>
            <DateOnly class="col-sm-5" v-model="entity.lastCO2Sensor" />
          </div>

          <div class="row mb-3" v-show="entity.hasThermometerCalibration && !isNew">
            <label class="col-form-label col-sm-4">Kalibracija termometra</label>
            <div class="col-sm-3">
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="thermometerCalibrationIntervalMonths"
                  id="thermometerCalibrationIntervalMonths1"
                  value="24"
                  v-model="entity.thermometerCalibrationIntervalMonths"
                />
                <label class="form-check-label" for="thermometerCalibrationIntervalMonths1">24</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="thermometerCalibrationIntervalMonths"
                  id="thermometerCalibrationIntervalMonths2"
                  value="720"
                  v-model="entity.thermometerCalibrationIntervalMonths"
                />
                <label class="form-check-label" for="thermometerCalibrationIntervalMonths2">Brez</label>
              </div>
            </div>
            <DateOnly class="col-sm-5" v-model="entity.lastThermometerCalibration" />
          </div>

          <div class="row mb-3" v-show="entity.hasSystemCheck && !isNew">
            <label class="col-form-label col-sm-4">Pregled sistema</label>
            <div class="col-sm-3">
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="systemCheckIntervalMonths"
                  id="systemCheckIntervalMonths1"
                  value="0"
                  v-model="entity.systemCheckIntervalMonths"
                />
                <label class="form-check-label" for="systemCheckIntervalMonths1">0</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="systemCheckIntervalMonths"
                  id="systemCheckIntervalMonths2"
                  value="12"
                  v-model="entity.systemCheckIntervalMonths"
                />
                <label class="form-check-label" for="systemCheckIntervalMonths2">12</label>
              </div>
            </div>
            <DateOnly class="col-sm-5" v-model="entity.lastSystemCheck" />
          </div>

          <div class="row mb-3" v-show="entity.hasAlarmTest && !isNew">
            <label class="col-form-label col-sm-4">Testiranje alarma v [mesec]</label>
            <div class="col-sm-3">
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="alarmTestIntervalMonths"
                  id="alarmTestIntervalMonths1"
                  value="0"
                  v-model="entity.alarmTestIntervalMonths"
                />
                <label class="form-check-label" for="alarmTestIntervalMonths1">0</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="alarmTestIntervalMonths"
                  id="alarmTestIntervalMonths2"
                  value="6"
                  v-model="entity.alarmTestIntervalMonths"
                />
                <label class="form-check-label" for="alarmTestIntervalMonths2">6</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="alarmTestIntervalMonths"
                  id="alarmTestIntervalMonths3"
                  value="12"
                  v-model="entity.alarmTestIntervalMonths"
                />
                <label class="form-check-label" for="alarmTestIntervalMonths3">12</label>
              </div>
            </div>
            <DateOnly class="col-sm-5" v-model="entity.lastAlarmTest" />
          </div>

          <div class="row mb-3" v-show="entity.hasLedomatCheck && !isNew">
            <label class="col-form-label col-sm-4">Pregled ledomata v [mesec]</label>
            <div class="col-sm-3">
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="ledomatCheckIntervalMonths"
                  id="ledomatCheckIntervalMonths1"
                  value="0"
                  v-model="entity.ledomatCheckIntervalMonths"
                />
                <label class="form-check-label" for="ledomatCheckIntervalMonths1">0</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="ledomatCheckIntervalMonths"
                  id="ledomatCheckIntervalMonths2"
                  value="6"
                  v-model="entity.ledomatCheckIntervalMonths"
                />
                <label class="form-check-label" for="ledomatCheckIntervalMonths2">6</label>
              </div>
              <div class="form-check form-check-inline">
                <input
                  class="form-check-input"
                  type="radio"
                  name="ledomatCheckIntervalMonths"
                  id="ledomatCheckIntervalMonths3"
                  value="12"
                  v-model="entity.ledomatCheckIntervalMonths"
                />
                <label class="form-check-label" for="ledomatCheckIntervalMonths3">12</label>
              </div>
            </div>
            <DateOnly class="col-sm-5" v-model="entity.lastLedomatCheck" />
          </div>

          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Opis</label>
            <div class="col-sm-8">
              <textarea class="form-control" rows="5" v-model="entity.description"></textarea>
            </div>
          </div>

          <div class="row mb-3">
            <label class="col-form-label col-sm-4">Posebnosti objekta</label>
            <div class="col-sm-8">
              <textarea class="form-control" rows="3" v-model="entity.facilityPeculiarities" readonly></textarea>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="card mb-3" v-if="!isNew && entity.facilityID !== $EMPTY_GUID">
            <div class="card-body">
              <h5 class="card-title d-none d-lg-block">Zadnji pregledi</h5>
              <p class="card-text" v-show="entity.lastLeakageTestObject">
                Tesnost: {{ entity.lastLeakageTestObject?.format($DATE_FORMAT_DISPLAY) }}
              </p>
              <p class="card-text" v-show="entity.lastServiceObject">
                Servis: {{ entity.lastServiceObject?.format($DATE_FORMAT_DISPLAY) }}
              </p>
              <p class="card-text" v-show="entity.lastCondenserCleaningObject">
                Čiščenje kondenzatorjev: {{ entity.lastCondenserCleaningObject?.format($DATE_FORMAT_DISPLAY) }}
              </p>
              <p class="card-text" v-show="entity.lastCO2SensorObject">
                CO2 senzor: {{ entity.lastCO2SensorObject?.format($DATE_FORMAT_DISPLAY) }}
              </p>
              <p class="card-text" v-show="entity.lastThermometerCalibrationObject">
                Kalibracija termometra: {{ entity.lastThermometerCalibrationObject?.format($DATE_FORMAT_DISPLAY) }}
              </p>
              <p class="card-text" v-show="canChange">
                <button class="btn btn-sm btn-primary" @click="showFacilityHistory = true">
                  Pretekli obiski na objektu
                </button>
              </p>
            </div>
          </div>

          <div class="card mb-3" v-show="entity.checklists?.length">
            <div class="card-body">
              <h5 class="card-title d-none d-lg-block">Kontrolni listi</h5>
              <!-- todo: duplicates around the app RAFACTOR -->
              <ul>
                <li
                  v-for="cr in entity.checklists.filter((c) => c.type === 'CalibrationReport')"
                  role="button"
                  class="text-decoration-underline"
                >
                  <a
                    :href="$AZURE_URI + '/Generated/CalibrationReport/' + cr.serviceOrderID + '?deviceId=' + entity.id"
                    target="_blank"
                    >{{ $dayjs(cr.dateCreated).format($DATE_FORMAT_DISPLAY) }}</a
                  >
                </li>
                <li
                  v-for="cr in entity.checklists.filter((c) => c.type === 'SystemReport')"
                  role="button"
                  class="text-decoration-underline"
                >
                  <a
                    :href="$AZURE_URI + '/Generated/SystemReport/' + cr.serviceOrderID + '?deviceId=' + entity.id"
                    target="_blank"
                  >
                    Kontrola delovanja hladilnega sistema {{ $dayjs(cr.dateCreated).format($DATE_FORMAT_DISPLAY) }}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="card mb-3" v-if="!isNew">
            <div class="card-body">
              <h5 class="card-title">Dokumentacija naprave</h5>
              <Attachments :attachments="attachments" filterByType="DeviceDocumentation" :showType="false" />
              <button v-if="canChange" class="btn btn-warning btn-sm ms-1 me-2" @click="showUpload = true">
                Naloži dokumentacijo naprave
              </button>
            </div>
          </div>

          <div class="card mb-3" v-if="!isNew">
            <div class="card-body">
              <h5 class="card-title">Dokumentacija objekta</h5>
              <Attachments :attachments="facilityAttachments" filterByType="FacilityDocumentation" :showType="false" />
            </div>
          </div>

          <div class="card mb-3" v-if="!isNew">
            <div class="card-body">
              <h5 class="card-title">Zgodovina</h5>
              <ul class="list-group list-group-flush">
                <li class="list-group-item" v-for="a in audit">
                  {{ $dayjs.utc(a.AuditDate).local().format($DATETIME_FORMAT_DISPLAY) }}
                  {{ a.UserModified }}
                  <span :title="a.IsDefrosting ? 'Defrosting on' : 'Defrosting off'">{{
                    a.IsDefrosting ? "🟢" : "⚪"
                  }}</span>
                  <span
                    class="text-muted"
                    v-if="
                      a.DefrostingNote &&
                      (typeof a.DefrostingNote !== 'object' || Object.keys(a.DefrostingNote).length > 0)
                    "
                  >
                    {{ a.DefrostingNote }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </fieldset>

    <FacilityHistory
      :facilityId="entity.facilityID"
      @close="showFacilityHistory = false"
      :visible="showFacilityHistory"
    />

    <hr v-show="entity?.hasAttributes" />

    <fieldset :disabled="!canChange" class="border-0 p-0 m-0">
      <div class="row mb-3" v-for="a in entity?.attributes">
        <label class="col-form-label col-sm-4">{{ translations.get(a.name) || a.name }}</label>
        <div class="col-sm-8" v-if="a.typeText === 'String'">
          <input class="form-control" type="text" v-model="a.value" />
        </div>
        <div class="col-sm-8" v-else-if="a.typeText === 'Enum' && a.options.length > 9">
          <select class="form-select" v-model="a.value">
            <option value=""></option>
            <option v-for="value in a.options" :value="value">{{ value }}</option>
          </select>
        </div>
        <div class="col-sm-8" v-else-if="a.typeText === 'Enum' && a.options.length < 10">
          <div class="form-check form-check-inline" v-for="(value, index) in a.options">
            <input
              class="form-check-input"
              type="radio"
              :name="a.name"
              :id="a.name + index"
              :value="value"
              v-model="a.value"
            />
            <label class="form-check-label" :for="a.name + index">{{ value }}</label>
          </div>
        </div>
        <div class="col-sm-8" v-else-if="a.typeText === 'EnumMulti'">
          <div class="form-check form-check-inline" v-for="(value, index) in a.options">
            <input
              class="form-check-input"
              type="checkbox"
              :name="a.name"
              :id="a.name + index"
              :value="value"
              :checked="isValueIncluded(a, value)"
              @change="(e) => (a.value = getEnumMultiValue(a.value, e.target.checked, value))"
            />
            <label class="form-check-label" :for="a.name + index">{{ value }}</label>
          </div>
        </div>
        <div class="col-sm-2" v-else-if="a.typeText === 'PositiveInt'">
          <input class="form-control" type="number" v-model="a.value" pattern="[0-9]+(?<!0)" />
        </div>
        <div class="col-sm-2" v-else-if="a.typeText === 'PositiveDecimal'">
          <input class="form-control" type="number" v-model="a.value" pattern="[0-9.]+" />
        </div>
        <div class="col-sm-8" v-else-if="a.typeText === 'Image'">
          <ImageInput v-model="a.value" allowed-type="image" />
        </div>
        <div class="col-sm-8" v-else-if="a.typeText === 'Boolean'">
          <div class="form-check form-switch">
            <input
              class="form-check-input"
              type="checkbox"
              :checked="a.value === 'true'"
              @change="(e) => (a.value = e.target.checked ? 'true' : 'false')"
            />
          </div>
        </div>
        <div class="col-sm-8" v-else>
          {{ a }}
        </div>
      </div>
    </fieldset>

    <RequiredWarning v-show="v.showWarning.value" :missing="v.missing.value" />

    <div class="row sticky-actions py-2 bg-light mb-5">
      <div class="col-2 text-start"></div>
      <div class="col-10 text-end">
        <button class="btn btn-outline-secondary me-3" type="button" @click="$router.push({ name: 'Devices' })">
          Prekliči
        </button>
        <button
          class="btn btn-success btn-lg me-3"
          type="button"
          @click="saveAsync"
          v-show="canChange"
        >
          Shrani
        </button>
      </div>
    </div>

    <ChangeHistory v-model="entity" v-show="!isNew" />

    <Upload
      v-if="canChange"
      :visible="!!showUpload"
      @close="showUpload = false"
      @add="
        (e) => {
          addAttachment(e)
        }
      "
      type="Device"
      attachmentType="DeviceDocumentation"
      :id="entity.id"
    />

    <div class="row mt-5" v-show="!isNew">
      <div class="col-md-6">
        <ServiceOrdersCard
          class="card mb-3"
          :deviceID="entity.id"
          :showLinkToMap="false"
          :showWhenEmpty="true"
          statuses="Completed,Finalized"
          title="Delovni nalogi"
          subTitle="Completed,Finalized"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { getDeviceAsync, saveEntityAsync } from "../api"
import { useWorkOrder } from "../composition-api/useWorkOrder"
import { DATETIME_FOR_LOCAL_INPUT } from "../utils/constants"
import { useEnums } from "../composition-api/useEnums"
import { useValidation, hasText, isSetGuid } from "../composition-api/useValidation"
import { useWorker } from "../composition-api/useWorker"
import {
  useAuth,
  authState,
  isAuthenticated,
  role,
  showMessageToWorker,
  currentWorkerId,
} from "../composition-api/useAuth"
import ImageInput from "../components/ImageInput.vue"
import FacilityHistory from "../components/FacilityHistory.vue"
import { useAttachments } from "../components/useAttachments"
import { toCultureIndependentString, parseNumberFromString } from "../utils/conversions"
import ServiceOrdersCard from "../components/ServiceOrdersCard.vue"
import DateOnly from "../components/DateOnly.vue"
import { setError, startLoading, stopLoading } from "../components/Notification"

defineOptions({ name: "DeviceEdit" })

const { deviceTypes, translations, loadEnumsAsync } = useEnums()
const route = VueRouter.useRoute()
const router = VueRouter.useRouter()
const { fetchAuthAsync } = useAuth()
const { worker } = useWorker()
const { attachments, addAttachment } = useAttachments()
const facilityAttachments = ref([])
const audit = ref([])
const showUpload = ref(false)
const entity = ref()
const showFacilityHistory = ref(false)

const isNew = computed(() => route.params.deviceId === "new")
const isCustomer = computed(() => !!role.value && role.value.startsWith("Customer"))
const isAdmin = computed(() => !!role.value && role.value.startsWith("Admin"))
const isLeader = computed(() => !!role.value && role.value.startsWith("Leader"))
const canChange = computed(() => !isCustomer.value && (isAdmin.value || isLeader.value || worker.isInventoryManager.value))

const v = useValidation(() => {
  const d = entity.value
  return {
    // a system has no label
    label: [() => hasText(d?.label) || d?.typeText === "System", "Oznaka - nalepka"],
    // shown on SelectCustomerEntity via :facility-invalid
    facilityID: [() => isSetGuid(d?.facilityID), "Objekt"],
    typeText: [() => d?.typeText !== "Unknown", "Vrsta naprave"],
    sourceDeviceID: [() => !d?.isSlave || isSetGuid(d?.sourceDeviceID), "Vezano na agregat"],
  }
})

const openDoorAsync = async () => {
  if (!canChange.value) {
    return
  }
  startLoading("openDoorAsync")
  try {
    const raw = await fetchAuthAsync(`${AZURE_URI}/Device/${entity.value.id}?openDoor=true`, "PATCH")
    if (raw?.status !== "ok") {
      throw raw
    }
    await loadEntityAsync()
  } catch (e) {
    setError(e)
  }
  stopLoading("openDoorAsync")
}

const loadEntityAsync = async () => {
  entity.value = await getDeviceAsync(fetchAuthAsync, route.params.deviceId)
  attachments.value = entity.value?.attachments
  facilityAttachments.value = entity.value?.facilityAttachments

  const raw = await fetchAuthAsync(`${AZURE_URI}/Audit/Devices/${entity.value.id}?field=IsDefrosting&limit=8`)
  if (raw?.status === "ok") {
    audit.value = raw.result
  } else {
    console.error("Error loading audit:", raw)
  }

  if (entity.value?.attributes) {
    entity.value.attributes.forEach((attr) => {
      if (attr.typeText === "PositiveInt" || attr.typeText === "PositiveDecimal") {
        attr.value = parseNumberFromString(attr.value)
      }
    })
  }
}

const saveAsync = async () => {
  if (!v.attempt()) return
  if (!canChange.value) {
    return
  }
  const entityToSave = JSON.parse(JSON.stringify(entity.value))
  if (entityToSave?.attributes) {
    entityToSave.attributes.forEach((attr) => {
      if (attr.typeText === "PositiveInt" || attr.typeText === "PositiveDecimal") {
        attr.value = toCultureIndependentString(attr.value)
      }
    })
  }

  const result = await saveEntityAsync(fetchAuthAsync, "Devices", entityToSave, isNew.value)
  if (!result) return
  if (isNew.value) {
    router.push({ name: "DeviceEdit", params: { deviceId: entity.value.id } })
  } else {
    await loadEntityAsync()
  }
}

const getEnumMultiValue = (currentValue, checked, value) => {
  let values = currentValue ? currentValue.split(",") : []
  if (checked) {
    if (!values.includes(value)) {
      values.push(value)
    }
  } else {
    const index = values.indexOf(value)
    if (index !== -1) {
      values.splice(index, 1)
    }
  }
  return values.join(",")
}

const isValueIncluded = (a, value) => {
  return a.value.split(",").includes(value)
}

watch(
  () => route.params.deviceId,
  () => loadEntityAsync(),
)

const DEVICE_TYPE_LABEL_PREFIX = {
  CoolingUnit: "AG",
  ColdRoomSystem: "K",
  ColdRoomStandalone: "Ks",
  FreezerRoomSystem: "MK",
  FreezerRoomStandalone: "MKs",
  PreparationRoom: "P",
  PreparationRoomPlugin: "Ps",
  PDVSystem: "PDV",
  PDVPlugin: "PDVs",
  PDVSelfSystem: "PDVSLF",
  PDVSelfPlugin: "PDVSLFs",
  PDVSemiVerticalSystem: "PDVSLFD",
  PDVSemiVerticalPlugin: "PDVSLFDs",
  SVOpenSystem: "SVO",
  SVOpenPlugin: "SVOs",
  SVClosedSystem: "SVZ",
  SVClosedPlugin: "SVZs",
  IslandMSystem: "OVM",
  IslandStandalone: "OVMs",
  IslandSystem: "OV",
  IslandPlugin: "OVs",
  VerticalFreezerSystem: "MV",
  VerticalFreezerPlugin: "MVs",
  HotCase: "TO",
  UndercounterCabinetSystem: "PH",
  UndercounterCabinetPlugin: "PHs",
  StainlessCabinetStandalone: "GO",
  HeatPump: "TČ",
  Chiller: "CH",
  Ledomat: "LED",
}

const entityLabelLeft = computed(() => DEVICE_TYPE_LABEL_PREFIX[entity.value?.typeText] ?? "")
const entityLabelRight = ref("")
watch([entityLabelLeft, entityLabelRight], ([left, right]) => {
  if (isNew.value && entity.value) {
    entity.value.label = `${left} ${right}`.trim()
  }
})

const { workOrder, devices, saveWorkOrderAsync, loadWorkOrderAsync } = useWorkOrder("Service", true, true)
const getLocationAsync = inject("getLocationAsync")
const createServiceOrderAsync = async () => {
  if (!canChange.value) {
    return
  }
  const location = await getLocationAsync()
  await loadWorkOrderAsync("new")
  workOrder.value.customerID = entity.value.customerID
  workOrder.value.facilityID = entity.value.facilityID
  devices.value = [{ id: entity.value.id, isRelated: true }]
  workOrder.value.subject = `Nov SDN za ${entity.value.deviceNumber}`
  workOrder.value.issueDescription = `Naprava: ${entity.value.label} (${entity.value.deviceNumber})\nObjekt: ${entity.value.facilityName}`
  await saveWorkOrderAsync(location)
}

onMounted(async () => {
  try {
    await loadEnumsAsync()
    await loadEntityAsync()
    if (isNew.value) {
      entity.value.facilityID = route.params.facilityId
      entity.value.customerID = route.params.customerId
    }
  } catch (e) {
    setError(e)
  }
})
</script>
